import _, { cloneDeep, partialRight } from 'lodash';
import { t } from '@lingui/macro';
import { i18n } from '@lingui/core';
import { COLUMN_TYPES } from '../data-table/DataTableColumns';
import { headerLinkRenderer } from './MatrixHeaderLink';
import { statusLinkRenderer } from './MatrixStatusLink';
import getMatrixTypeConfig, {
    AGENT_TYPE,
    BUSINESS_SERVICE_TYPE,
    SERVICE_TEST_TYPE,
    SITE_TYPE,
} from './MatrixConfigHelper';
import { MaterialUiIcon } from '../icon';
import { getStatusName } from '../../utilities/StatusUtilities';
import { middleEllipsis } from '../../utilities/StringUtilities';
import { headerRenderer } from './MatrixHeader';
import { timeRangeFilterId } from '../filters';

export const MATRIX_MAX_COLUMNS = 25;
const STATUS_COLUMN_WIDTH = 32;

export default function buildGridConfig(matrixData, { type, pivot }, filterState, classes) {
    const filterTime = cloneDeep(filterState?.current?.[timeRangeFilterId]);
    const typesConfig = getMatrixTypeConfig();
    const [rowType, columnType] = type.split(',');
    const columns = buildFirstColumns(rowType, classes);

    function getLinkConfig(rowOrColumn, rowOrColumnType) {
        return {
            ..._.get(typesConfig, `${rowOrColumn}.${rowOrColumnType}.link`, {}),
            filterState,
        };
    }

    function getCellLinkConfig(item) {
        const linkConfig = {
            ..._.get(typesConfig, `cellConfig.${rowType}.matrixCellLink`, {}),
            filterState,
        };
        const secondaryFilter = _.get(typesConfig, `cellConfig.${rowType}.matrixCellSecondaryFilterId`);
        _.set(linkConfig, `staticFilterParams.${secondaryFilter}`, { uuid: item.uuid });

        return linkConfig;
    }

    function buildFirstColumns() {
        const firstColumns = [
            {
                type: COLUMN_TYPES.DRILLINLINK,
                title: _.get(typesConfig, `rowConfig.${rowType}.title`, ''),
                getTooltip: (row) => i18n._(t`View Details for ${_.get(row, `${rowType}.name`)}`),
                headerRenderer: partialRight(headerRenderer, true, 0),
                headerClassName: classes.matrixNormalHeader,
                className: classes.matrixFirstCell,
                width: 280,
                mapping: `${rowType}.name`,
                link: getLinkConfig('rowConfig', rowType),
                formatter: (value) => middleEllipsis(value, 35),
                statusSortField: pivot,
                nameSortField: _.get(typesConfig, `rowConfig.${rowType}.nameSortField`, ''),
            },
        ];

        if (rowType === SERVICE_TEST_TYPE) {
            firstColumns.push({
                type: COLUMN_TYPES.ICON,
                title: '',
                width: 35,
                className: classes.matrixTestTypeCell,
                resizable: false,
                icons: [
                    {
                        getIcon: (row) => <MaterialUiIcon icon={row?.test?.typeNode?.details?.icon} />,
                    },
                ],
                getTooltip: (row) => row?.test?.typeNode?.details?.title,
            });
        }

        return firstColumns;
    }

    function buildStatusColumns() {
        return [
            {
                type: COLUMN_TYPES.STATUS,
                width: STATUS_COLUMN_WIDTH,
                title: t`Overall Status`,
                headerClassName: classes.matrixVerticalHeader,
                className: classes.matrixDataCell,
                mapping: 'status',
                renderer: statusLinkRenderer,
                getTooltip: getOverallStatusTooltip,
                link: getLinkConfig('rowConfig', rowType),
                resizable: false,
            },
            ...buildStatusDetailColumns(),
        ];
    }

    function buildStatusDetailColumns() {
        return [
            {
                type: COLUMN_TYPES.STATUS,
                width: STATUS_COLUMN_WIDTH,
                title: t`Service Tests`,
                headerClassName: classes.matrixVerticalHeader,
                className: classes.matrixDataCell,
                mapping: 'bsTestExperience',
                renderer: statusLinkRenderer,
                getTooltip: getDetailsStatusTooltip('bsTestExperience', 'Service Tests'),
                link: getLinkConfig('rowConfig', 'test-drill'),
                resizable: false,
                removable: true,
            },
            {
                type: COLUMN_TYPES.STATUS,
                width: STATUS_COLUMN_WIDTH,
                title: t`Servers`,
                headerClassName: classes.matrixVerticalHeader,
                className: classes.matrixDataCell,
                mapping: 'bsStatusServers',
                renderer: statusLinkRenderer,
                getTooltip: getDetailsStatusTooltip('bsStatusServers', 'Servers'),
                link: getLinkConfig('rowConfig', 'server-drill'),
                resizable: false,
                removable: true,
            },
            {
                type: COLUMN_TYPES.STATUS,
                width: STATUS_COLUMN_WIDTH,
                title: t`Network Devices`,
                headerClassName: classes.matrixVerticalHeader,
                className: classes.matrixDataCell,
                mapping: 'bsStatusDevices',
                renderer: statusLinkRenderer,
                getTooltip: getDetailsStatusTooltip('bsStatusDevices', 'Network Devices'),
                link: getLinkConfig('rowConfig', 'device-drill'),
                resizable: false,
                removable: true,
            },
        ];
    }

    function buildColumnsTitle() {
        return {
            type: COLUMN_TYPES.TEXT,
            width: 46,
            title: _.get(typesConfig, `columnConfig.${columnType}.title`, ''),
            headerClassName: classes.matrixVerticalHeader,
            headerRenderer: partialRight(headerRenderer, false, 1),
            className: classes.matrixDataCell,
            mapping: '',
            formatter: () => '',
            statusSortField: pivot,
            nameSortField: _.get(typesConfig, `columnConfig.${columnType}.nameSortField`, ''),
            resizable: false,
            removable: false,
        };
    }

    function buildColumn(item) {
        return {
            type: COLUMN_TYPES.STATUS,
            width: STATUS_COLUMN_WIDTH,
            title: middleEllipsis(item.name, 20),
            headerClassName: classes.matrixRotateHeader,
            headerRenderer: headerLinkRenderer,
            getHeaderTooltip: () => i18n._(t`View Details for ${item.name}`),
            headerLink: getLinkConfig('columnConfig', columnType),
            headerLinkData: { [columnType]: item },
            className: classes.matrixDataCell,
            mapping: `${item.order}.${pivot}`,
            getTooltip: partialRight(getCellTooltip, item),
            renderer: statusLinkRenderer,
            link: getCellLinkConfig(item),
            resizable: false,
            removable: false,
        };
    }

    function buildMoreColumn() {
        const moreConfig = buildMoreColumnConfig();

        return {
            type: COLUMN_TYPES.TEXT,
            width: 115,
            title: i18n._(moreConfig.title),
            align: 'right',
            headerRenderer: headerLinkRenderer,
            className: classes.matrixDataCell,
            headerTooltip: i18n._(moreConfig.title),
            headerLink: moreConfig.link,
            mapping: '',
            formatter: () => '',
            resizable: false,
        };
    }

    function buildMoreColumnConfig() {
        if (columnType === SITE_TYPE) {
            return {
                title: t`More Sites`,
                link: {
                    target: '/sites',
                    staticFilterParams: {
                        [timeRangeFilterId]: filterTime,
                    },
                },
                removable: true,
            };
        }

        return {
            title: t`More nPoints`,
            link: {
                target: '/agents',
                staticFilterParams: {
                    [timeRangeFilterId]: filterTime,
                },
            },
            removable: true,
        };
    }

    function getOverallStatusTooltip(row, column) {
        const tooltip = [];

        // Add the status breakouts to the tooltip if the corresponding columns are NOT shown in the matrix.
        tooltip.push(t`Status: ${getStatusName(column.mapper(row, column)?.worst)}`);
        tooltip.push(t`Service Tests: ${getStatusName(_.get(row, 'bsTestExperience.worst'))}`);
        tooltip.push(t`Servers: ${getStatusName(_.get(row, 'bsStatusServers.worst'))}`);
        tooltip.push(t`Network Devices: ${getStatusName(_.get(row, 'bsStatusDevices.worst'))}`);
        tooltip.push(t`View Overall Status for ${_.get(row, `${BUSINESS_SERVICE_TYPE}.name`)}`);

        return tooltip.map((text) => i18n._(text)).join('\r\n');
    }

    function getDetailsStatusTooltip(metric, label) {
        return (row) => {
            const tooltip = [];

            // Add the status breakouts to the tooltip if the corresponding columns are NOT shown in the matrix.
            const val = _.get(row, `${metric}.worst`);
            tooltip.push(t`Status: ${getStatusName(val)}`);
            tooltip.push(`View ${label} for ${_.get(row, `${BUSINESS_SERVICE_TYPE}.name`)}`);
            return tooltip.map((text) => i18n._(text)).join('\r\n');
        };
    }

    function getCellTooltip(row, column, columnItem) {
        const status = column.mapper(row, column);
        if (!status) {
            return getNotConfiguredCellTooltip(row, column, columnItem);
        }

        const columnLabel = i18n._(_.get(typesConfig, `columnConfig.${columnType}.titleSingular`, ''));
        const rowLabel = i18n._(_.get(typesConfig, `rowConfig.${rowType}.titleSingular`, ''));
        const viewText = rowType === SERVICE_TEST_TYPE ? t`View Test Log for ` : t`View Service Tests for `;

        // TODO: titleRows.push(`${$i18next.t(colAndRowMappings.cell.isCurrent ? 'matrix.currentStatusTooltip' : 'matrix.statusTooltip')}: ${ statusName }`);

        return [
            t`Worst Status: ${getStatusName(status?.worst)}`,
            viewText,
            t`${rowLabel}: ${_.get(row, `${rowType}.name`)}`,
            t`${columnLabel}: ${columnItem.name}`,
        ]
            .map((text) => i18n._(text))
            .join('\r\n');
    }

    function getNotConfiguredCellTooltip(row, column, columnItem) {
        const notConfigured = t`Not Configured`;
        const columnLabel = i18n._(_.get(typesConfig, `rowConfig.${rowType}.titleSingular`, ''));
        const rowLabel = i18n._(_.get(typesConfig, `columnConfig.${columnType}.titleSingular`, ''));

        return [notConfigured, t`${rowLabel}: ${_.get(row, `${rowType}.name`)}`, t`${columnLabel}: ${columnItem.name}`]
            .map((text) => i18n._(text))
            .join('\r\n');
    }

    function buildFlexColumn() {
        return {
            type: COLUMN_TYPES.TEXT,
            flex: 1,
            title: '',
            className: classes.matrixDataCell,
            mapping: '',
            formatter: () => '',
            removable: false,
        };
    }

    if (!matrixData) {
        return null;
    }

    if (rowType === BUSINESS_SERVICE_TYPE) {
        columns.push(...buildStatusColumns(rowType, classes));
    }

    columns.push(buildColumnsTitle(columnType, classes));

    // Build the main part of the matrix
    matrixData.columns.forEach((item) => columns.push(buildColumn(item)));

    // If we have more sites or nPoints than we can show on the page, give them a link to see the rest.
    if (
        _.get(matrixData, 'columnCount', 0) > MATRIX_MAX_COLUMNS &&
        (columnType === SITE_TYPE || columnType === AGENT_TYPE)
    ) {
        columns.push(buildMoreColumn());
    }

    // Extra flex column so we can control the border
    columns.push(buildFlexColumn());

    return columns;
}

export function defaultColumnOverrides({ type }) {
    const [rowType] = type.split(',');
    return rowType === BUSINESS_SERVICE_TYPE
        ? [
              {
                  id: t`Service Tests`,
                  hidden: true,
              },
              {
                  id: t`Servers`,
                  hidden: true,
              },
              {
                  id: t`Network Devices`,
                  hidden: true,
              },
          ]
        : [];
}
